// The number on the sidebar's Email item: unread messages in the inbox,
// mailbox-wide — same figure the Email page's Inbox/Unread tabs show (both
// come from emailCounts, one HEAD count against the table). The Text Inbox
// badge next to it is a per-browser "seen" marker; email read-state is shared
// (a row's `read` flag), so this one is simply the server's answer.
//
// Live where it can be: a postgres_changes subscription on `emails` (a sync
// inserting rows, anyone marking a message read) re-counts after a short
// debounce; a slow interval backs it up in case the table is not in the
// realtime publication. Firestore mode has no counter — badge stays hidden.
// The subscription rides the shared per-tab `emails` binding (services/
// tableHub) — the alerts watcher, the cache and the inbox page use the same
// one, so the table costs Realtime one policy check per change per tab.
import { useEffect, useState } from 'react';
import { emailCounts } from './emailCache';
import { USE_SUPABASE } from './supabaseClient';
import { subscribeTableHub } from './tableHub';

const POLL_MS = 60_000;
// One recount for a sync batch (Phase 1, 2026-09-30: 800 ms split a batch into several recounts per tab).
const DEBOUNCE_MS = 3_000;

export function subscribeUnreadEmailCount(onCount: (n: number) => void): () => void {
  if (!USE_SUPABASE) return () => {};
  let stopped = false;
  const refresh = () => {
    emailCounts()
      .then((c) => { if (!stopped && c) onCount(c.unread); })
      .catch(() => {}); // a failed count keeps the last number; never throws into the sidebar
  };
  // A hidden tab shows no sidebar: its backup poll is skipped (the realtime
  // nudge still recounts on a real change) and one recount runs the moment
  // the tab is looked at again (audit, 2026-09-25: this was two count
  // queries a minute per background tab, for a number nobody could see).
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  refresh();
  const timer = setInterval(() => { if (!hidden()) refresh(); }, POLL_MS);
  let debounce: ReturnType<typeof setTimeout> | null = null;
  // A change while the tab is hidden is held and recounted once the tab is shown (Phase 1, 2026-09-30):
  // every emails change used to run two exact counts in every open tab, seen or not.
  let heldWhileHidden = false;
  const schedule = () => {
    if (hidden()) { heldWhileHidden = true; return; }
    if (debounce) clearTimeout(debounce);
    debounce = setTimeout(refresh, DEBOUNCE_MS); // a sync batch is many rows; one recount covers it
  };
  const onVisible = () => {
    if (hidden()) return;
    if (heldWhileHidden) { heldWhileHidden = false; schedule(); }
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  const leave = subscribeTableHub('emails', { onChange: schedule });
  return () => {
    stopped = true;
    clearInterval(timer);
    if (debounce) clearTimeout(debounce);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    leave();
  };
}

/** Sidebar hook. `enabled` = the Email item is actually shown to this user
 *  (feature on + role allowed) — no subscription for a badge nobody can see. */
export function useUnreadEmailCount(enabled: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) { setN(0); return; }
    return subscribeUnreadEmailCount(setN);
  }, [enabled]);
  return n;
}
